import { memo, ReactNode, useMemo } from 'react'
import clsx from 'clsx'

import {
  RadioGroupContext,
  RadioGroupOrientation,
  RadioGroupVariant
} from './context'

import styles from './RadioGroup.module.scss'

export interface RadioGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  name: string
  selectedValue: string | null
  onChange: (value: string, event?: React.ChangeEvent<HTMLInputElement>) => void
  orientation?: RadioGroupOrientation
  hasRadioMark?: boolean
  variant?: RadioGroupVariant
  children: ReactNode
}

const RadioGroup = memo(
  ({
    name,
    selectedValue,
    onChange,
    orientation = 'vertical',
    hasRadioMark = true,
    variant = 'unboxed',
    children,
    className,
    ...rest
  }: RadioGroupProps) => {
    const contextValue = useMemo(
      () => ({
        name,
        selectedValue,
        orientation,
        hasRadioMark,
        variant,
        onChange
      }),
      [name, selectedValue, orientation, variant, hasRadioMark, onChange]
    )

    return (
      <RadioGroupContext.Provider value={contextValue}>
        <div
          role='radiogroup'
          aria-orientation={orientation}
          {...rest}
          className={clsx(
            styles.radioGroup,
            { [styles.horizontal]: orientation === 'horizontal' },
            className
          )}
        >
          {children}
        </div>
      </RadioGroupContext.Provider>
    )
  }
)

export default RadioGroup
